import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";

/** Mirrors brian's `StageStatus`. */
export type StageStatus = components["schemas"]["StageStatus"];

interface VisitStage {
  id: string;
  position: number;
  name: string;
  status: StageStatus;
  enteredAt: string | null;
  completedAt: string | null;
}

/** Provenance, not derived: brian decides both boards in the clinic's timezone. */
export type VisitBoard = "today" | "open";

export interface TodayVisit {
  id: string;
  patientId: string;
  patientName: string;
  clientId: string | null;
  clientName: string | null;
  householdId: string;
  householdLabel: string;
  assignedUserId: string | null;
  assignedUserName: string | null;
  currentRoomUuid: string | null;
  visitType: string;
  flow: components["schemas"]["VisitFlow"];
  position: components["schemas"]["VisitPosition"];
  progressRevision: number;
  progressAt: string | null;
  reason: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  /** Cancel is only allowed before the visit is underway, so `currentStage` is null. */
  cancelledAt: string | null;
  createdAt: string;
  currentStage: VisitStage | null;
  stages: VisitStage[];
  roomConfirmation: RoomConfirmation | null;
  /** Client-side provenance, not a wire field; null off-board. */
  board: VisitBoard | null;
}

/** Mirrors brian's `RoomConfirmationBrief` (modules/room_confirmation/schemas.py): the visit's open confirmation. */
interface RoomConfirmation {
  roomId: string;
  method: components["schemas"]["RoomConfirmationBrief"]["method"];
  confirmedAt: string;
  confirmedByName: components["schemas"]["RoomConfirmationBrief"]["confirmed_by_name"];
}

/** The backend 422s on half a cursor. */
export interface VisitHistoryCursor {
  before: string;
  beforeUuid: string;
}

export { isConfirmedIn, isVisitCancelled } from "./visitStatus";

const toVisitStage = (
  s: components["schemas"]["VisitStageOut"],
): VisitStage => ({
  id: s.uuid,
  position: s.position,
  name: s.name,
  status: s.status,
  enteredAt: s.entered_at,
  completedAt: s.completed_at,
});

export const toTodayVisit = (
  v: components["schemas"]["TodayVisitOut"],
  board: VisitBoard | null,
): TodayVisit => ({
  id: v.uuid,
  patientId: v.patient_uuid,
  patientName: v.patient_name,
  clientId: v.client_uuid,
  clientName: v.client_name,
  householdId: v.household_uuid,
  householdLabel: v.household_label,
  assignedUserId: v.assigned_user_uuid,
  assignedUserName: v.assigned_user_name,
  currentRoomUuid: v.current_room_uuid,
  visitType: v.visit_type,
  flow: v.flow,
  position: v.position,
  progressRevision: v.progress_revision,
  progressAt: v.progress_at,
  reason: v.reason,
  scheduledStart: v.scheduled_start,
  scheduledEnd: v.scheduled_end,
  cancelledAt: v.cancelled_at,
  createdAt: v.created_at,
  currentStage: v.current_stage ? toVisitStage(v.current_stage) : null,
  stages: [...v.stages]
    .sort((a, b) => a.position - b.position)
    .map(toVisitStage),
  roomConfirmation: v.room_confirmation
    ? {
        roomId: v.room_confirmation.room_uuid,
        method: v.room_confirmation.method,
        confirmedAt: v.room_confirmation.confirmed_at,
        confirmedByName: v.room_confirmation.confirmed_by_name,
      }
    : null,
  board,
});

export const VISIT_HISTORY_LIMIT = 50;

export const visitsClient = {
  setProgress: (visit: TodayVisit, position: TodayVisit["position"]) =>
    request(() =>
      apiClient.POST("/visits/{visit_uuid}/progress", {
        params: { path: { visit_uuid: visit.id } },
        body: { position, expected_revision: visit.progressRevision },
      }),
    ),
  today: (): Promise<TodayVisit[]> =>
    request(() => apiClient.GET("/visits/today")).then((rows) =>
      rows.map((row) => toTodayVisit(row, "today")),
    ),

  /** Earlier days' unfinished visits; rule and lookback are brian's `VisitService.unfinished_before_today`. */
  open: (): Promise<TodayVisit[]> =>
    request(() => apiClient.GET("/visits/open")).then((rows) =>
      rows.map((row) => toTodayVisit(row, "open")),
    ),

  /** Newest first. */
  forPatient: (
    patientUuid: string,
    cursor?: VisitHistoryCursor,
  ): Promise<TodayVisit[]> =>
    request(() =>
      apiClient.GET("/patients/{patient_uuid}/visits", {
        params: {
          path: { patient_uuid: patientUuid },
          query: {
            limit: VISIT_HISTORY_LIMIT,
            before: cursor?.before,
            before_uuid: cursor?.beforeUuid,
          },
        },
      }),
    ).then((rows) => rows.map((row) => toTodayVisit(row, null))),
};
